import React, { useState, useEffect } from "react";
import {
  signIn,
  checkEmail,
  signInWithGoogle,
  signInWithFacebook,
} from "../cognitoService";
import { useNavigate, useLocation } from "react-router-dom";
import { getClientName, isAllowedRedirectDomain } from "../utils/clientNames";
import Footer from "../components/Footer";

export default function Login() {
  const [step, setStep] = useState("email"); // 'email' | 'password'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [redirectToClient, setRedirectToClient] = useState("");
  const [isValidClient, setIsValidClient] = useState(false);
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const redirectParam = urlParams.get("redirectToClient");

    if (redirectParam && isAllowedRedirectDomain(redirectParam)) {
      setRedirectToClient(redirectParam);
      setIsValidClient(true);
    } else {
      setIsValidClient(false);
      setError("Client ID not found or invalid");
    }
  }, [location.search]);

  const handleBackToClient = () => {
    window.location.href = `https://${redirectToClient}`;
  };

  const handleGoogleSignIn = () => {
    setGoogleLoading(true);
    signInWithGoogle(redirectToClient);
  };

  const handleFacebookSignIn = () => {
    setFacebookLoading(true);
    signInWithFacebook(redirectToClient);
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!isValidClient || emailLoading) return;

    if (!isValidEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setEmailLoading(true);
    setError("");

    try {
      const result = await checkEmail(email);
      if (result.authMethod === "sso") {
        let ssoUrl = result.ssoRedirectUrl;
        if (redirectToClient) {
          const state = encodeURIComponent(JSON.stringify({ redirectToClient }));
          ssoUrl += `&state=${state}`;
        }
        window.location.href = ssoUrl;
      } else {
        setStep("password");
      }
    } catch (err) {
      // Fail open - proceed to password step even if check fails
      setStep("password");
    } finally {
      setEmailLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setPassword("");
    setError("");
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!isValidClient || loading) return;

    setLoading(true);
    setError("");

    try {
      await signIn({ email, password, redirectToClient });
      if (!redirectToClient) {
        nav("/home");
      }
    } catch (err) {
      setError(err.message || JSON.stringify(err));
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 16,
    backgroundColor: "#ffffff",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
    outline: "none",
  };

  const inputStyleDisabled = {
    ...inputStyle,
    backgroundColor: "#f9fafb",
  };

  const primaryButtonStyle = (isLoading) => ({
    width: "100%",
    padding: "12px 24px",
    backgroundColor: isLoading ? "#94a3b8" : "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 500,
    cursor: isLoading ? "not-allowed" : "pointer",
    transition: "background-color 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  });

  const LoadingSpinner = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ animation: "spin 1s linear infinite" }}
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#f8fafc",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        overflow: "auto",
      }}
    >
      <div
        style={{
          maxWidth: 460,
          width: "100%",
          padding: 40,
          backgroundColor: "white",
          borderRadius: 16,
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          textAlign: "center",
        }}
      >
        <div style={{ marginBottom: 32 }}>
          <img
            src="/logo.svg"
            alt="Berg Logo"
            style={{
              width: 120,
              height: "auto",
              marginBottom: 16,
            }}
          />
          <h1
            style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 600,
              color: "#1e293b",
              marginBottom: 8,
            }}
          >
            Berg Unified Login
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: 16,
              color: "#64748b",
            }}
          >
            Sign in to your account
          </p>
        </div>

        {error && (
          <div
            style={{
              backgroundColor: "#fef2f2",
              color: "#dc2626",
              padding: 12,
              borderRadius: 8,
              marginBottom: 24,
              fontSize: 14,
              border: "1px solid #fecaca",
            }}
          >
            {error}
          </div>
        )}

        {isValidClient && step === "email" && (
          <>
            <form onSubmit={handleEmailSubmit} style={{ textAlign: "left" }}>
              <div style={{ marginBottom: 20 }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={emailLoading}
                  style={emailLoading ? inputStyleDisabled : inputStyle}
                  onFocus={(e) =>
                    !emailLoading && (e.target.style.borderColor = "#3b82f6")
                  }
                  onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                  placeholder="Email"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={emailLoading}
                style={primaryButtonStyle(emailLoading)}
                onMouseOver={(e) => {
                  if (!emailLoading) e.target.style.backgroundColor = "#2563eb";
                }}
                onMouseOut={(e) => {
                  if (!emailLoading) e.target.style.backgroundColor = "#3b82f6";
                }}
              >
                {emailLoading ? (
                  <LoadingSpinner />
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
                  </svg>
                )}
                {emailLoading ? "Checking..." : "Continue"}
              </button>
              <style>
                {`
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}
              </style>
            </form>

            <div style={{ margin: "24px 0", textAlign: "center" }}>
              <div
                style={{
                  borderTop: "1px solid #e5e7eb",
                  position: "relative",
                  margin: "24px 0",
                }}
              >
                <span
                  style={{
                    background: "white",
                    padding: "0 16px",
                    position: "absolute",
                    top: "-10px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    color: "#64748b",
                    fontSize: 14,
                  }}
                >
                  or
                </span>
              </div>
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
                style={{
                  width: "100%",
                  padding: "12px 24px",
                  backgroundColor: googleLoading ? "#f9fafb" : "#ffffff",
                  color: googleLoading ? "#9ca3af" : "#374151",
                  border: `1px solid ${googleLoading ? "#e5e7eb" : "#d1d5db"}`,
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: 500,
                  cursor: googleLoading ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
                onMouseOver={(e) => {
                  if (!googleLoading) {
                    e.target.style.backgroundColor = "#f9fafb";
                    e.target.style.borderColor = "#9ca3af";
                  }
                }}
                onMouseOut={(e) => {
                  if (!googleLoading) {
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.borderColor = "#d1d5db";
                  }
                }}
              >
                {googleLoading ? (
                  <LoadingSpinner />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path
                      fill="#4285f4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34a853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#fbbc05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#ea4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                )}
                {googleLoading
                  ? "Redirecting to Google..."
                  : "Sign in with Google"}
              </button>
              <button
                type="button"
                onClick={handleFacebookSignIn}
                disabled={facebookLoading}
                style={{
                  width: "100%",
                  padding: "12px 24px",
                  backgroundColor: facebookLoading ? "#f9fafb" : "#ffffff",
                  color: facebookLoading ? "#9ca3af" : "#374151",
                  border: `1px solid ${facebookLoading ? "#e5e7eb" : "#d1d5db"}`,
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: 500,
                  cursor: facebookLoading ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  marginTop: 12,
                }}
                onMouseOver={(e) => {
                  if (!facebookLoading) {
                    e.target.style.backgroundColor = "#f9fafb";
                    e.target.style.borderColor = "#9ca3af";
                  }
                }}
                onMouseOut={(e) => {
                  if (!facebookLoading) {
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.borderColor = "#d1d5db";
                  }
                }}
              >
                {facebookLoading ? (
                  <LoadingSpinner />
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="#1877F2"
                    aria-hidden="true"
                  >
                    <path d="M22 12.06C22 6.48 17.52 2 11.94 2S2 6.48 2 12.06c0 5.01 3.66 9.16 8.44 9.94v-7.03H8.08v-2.91h2.36V9.41c0-2.33 1.39-3.62 3.53-3.62 1.02 0 2.1.18 2.1.18v2.31h-1.18c-1.16 0-1.52.72-1.52 1.46v1.76h2.59l-.41 2.91h-2.18V22c4.78-.78 8.44-4.93 8.44-9.94z" />
                  </svg>
                )}
                {facebookLoading
                  ? "Redirecting to Facebook..."
                  : "Sign in with Facebook"}
              </button>

              {getClientName(redirectToClient) && (
                <>
                  <div
                    style={{
                      borderTop: "1px solid #e5e7eb",
                      position: "relative",
                      margin: "24px 0",
                    }}
                  >
                    <span
                      style={{
                        background: "white",
                        padding: "0 16px",
                        position: "absolute",
                        top: "-10px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        color: "#64748b",
                        fontSize: 14,
                      }}
                    >
                      or
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleBackToClient}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      backgroundColor: "#f8fafc",
                      color: "#64748b",
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 400,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = "#f1f5f9";
                      e.target.style.borderColor = "#cbd5e1";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = "#f8fafc";
                      e.target.style.borderColor = "#e5e7eb";
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                    </svg>
                    Get back to {getClientName(redirectToClient)}
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {isValidClient && step === "password" && (
          <>
            <div style={{ textAlign: "left", marginBottom: 20 }}>
              <button
                type="button"
                onClick={handleBackToEmail}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "8px 0",
                  background: "none",
                  border: "none",
                  color: "#3b82f6",
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: "pointer",
                  marginBottom: 16,
                }}
                onMouseOver={(e) => (e.target.style.color = "#2563eb")}
                onMouseOut={(e) => (e.target.style.color = "#3b82f6")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                </svg>
                Back
              </button>

              <div
                style={{
                  backgroundColor: "#f8fafc",
                  padding: "12px 16px",
                  borderRadius: 8,
                  marginBottom: 20,
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: 13,
                    color: "#64748b",
                    marginBottom: 4,
                  }}
                >
                  Signing in as
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: 15,
                    fontWeight: 500,
                    color: "#1e293b",
                  }}
                >
                  {email}
                </p>
              </div>
            </div>

            <form onSubmit={handlePasswordSubmit} style={{ textAlign: "left" }}>
              <div style={{ marginBottom: 24 }}>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  style={loading ? inputStyleDisabled : inputStyle}
                  onFocus={(e) =>
                    !loading && (e.target.style.borderColor = "#3b82f6")
                  }
                  onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                  placeholder="Password"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{ ...primaryButtonStyle(loading), marginBottom: 20 }}
                onMouseOver={(e) => {
                  if (!loading) e.target.style.backgroundColor = "#2563eb";
                }}
                onMouseOut={(e) => {
                  if (!loading) e.target.style.backgroundColor = "#3b82f6";
                }}
              >
                {loading ? (
                  <LoadingSpinner />
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                )}
                {loading ? "Signing In..." : "Sign In"}
              </button>
              <style>
                {`
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}
              </style>
            </form>

            <div style={{ textAlign: "center" }}>
              <a
                href={`/forgot-password?email=${encodeURIComponent(email)}${
                  redirectToClient
                    ? `&redirectToClient=${encodeURIComponent(redirectToClient)}`
                    : ""
                }`}
                style={{
                  color: "#3b82f6",
                  fontSize: 16,
                  fontWeight: 500,
                  textDecoration: "none",
                  transition: "color 0.2s",
                  padding: "8px 0",
                  display: "inline-block",
                }}
                onMouseOver={(e) => (e.target.style.color = "#2563eb")}
                onMouseOut={(e) => (e.target.style.color = "#3b82f6")}
              >
                Forgot password?
              </a>
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
